/**
 * Wear It — Operating System navigation (Dashboard Redesign, Phase 1)
 *
 * Re-groups every EXISTING dashboard page into 9 workflow-first workspaces.
 * No route is removed — each item points at the same path it does today, just
 * re-homed into the workspace a real job maps to. Gated behind features.NEW_NAV
 * (see configs/navigation.config/index.js). Reverting the flag restores the
 * original flat "APPS" sidebar with zero data loss.
 */
import { APP_PREFIX_PATH } from 'constants/route.constant'
import {
    NAV_ITEM_TYPE_TITLE,
    NAV_ITEM_TYPE_COLLAPSE,
    NAV_ITEM_TYPE_ITEM,
} from 'constants/navigation.constant'
import { ADMIN, SUPPORT, SHOPPER } from 'constants/roles.constant'
import features from '../features'

const item = (key, path, title, authority = [ADMIN]) => ({
    key,
    path,
    title,
    translateKey: `nav.wearit.${key}`,
    icon: '',
    type: NAV_ITEM_TYPE_ITEM,
    authority,
    subMenu: [],
})

// Refined nav (audit P1-6): list pages own their create buttons, so the
// nav drops the six standalone "create" rows — unless the flag is off.
const refined = features.REFINED_NAV
const createRow = (...args) => (refined ? [] : [item(...args)])

const wearitNavigationConfig = [
    // ── HOME ──────────────────────────────────────────────────────────
    {
        key: 'wearit.home',
        path: `${APP_PREFIX_PATH}/home`,
        title: 'Home',
        translateKey: 'nav.wearit.home',
        icon: 'commandCentre',
        type: NAV_ITEM_TYPE_ITEM,
        authority: [ADMIN],
        subMenu: [],
    },

    // ── WORKSPACES section label ──────────────────────────────────────
    {
        key: 'wearit.section.workspaces',
        path: '',
        title: 'Workspaces',
        translateKey: 'nav.wearit.section.workspaces',
        icon: '',
        type: NAV_ITEM_TYPE_TITLE,
        authority: [ADMIN, SUPPORT, SHOPPER],
        subMenu: [
            // ── ORDERS ────────────────────────────────────────────────
            {
                key: 'wearit.orders',
                path: '',
                title: 'Orders',
                translateKey: 'nav.wearit.orders',
                icon: 'orders',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: [ADMIN],
                subMenu: [
                    item('orders.list', `${APP_PREFIX_PATH}/sales/order-list`, 'All Orders'),
                    item('orders.reservations', `${APP_PREFIX_PATH}/sales/reservations`, 'Reservations'),
                    item('orders.fulfilment', `${APP_PREFIX_PATH}/sales/shipping`, 'Fulfilment'),
                    item('orders.payouts', `${APP_PREFIX_PATH}/sales/payouts`, 'Payouts'),
                    item('orders.disputes', `${APP_PREFIX_PATH}/sales/disputes`, 'Disputes'),
                    item('orders.exchange', `${APP_PREFIX_PATH}/sales/exchange`, 'Exchange'),
                    item('orders.paymentLinks', `${APP_PREFIX_PATH}/sales/payment-links`, 'Payment Links'),
                ],
            },

            // ── CATALOG ───────────────────────────────────────────────
            {
                key: 'wearit.catalog',
                path: '',
                title: 'Catalog',
                translateKey: 'nav.wearit.catalog',
                icon: 'catalog',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: [ADMIN],
                // Audit 2026-08: names say what each record IS — a Product Card
                // is the one customer-facing record; Stock & Sources are the
                // supply listings beneath it. Same routes, clearer words.
                subMenu: [
                    item('catalog.productsDirectory', `${APP_PREFIX_PATH}/catalog/products`, 'Product Cards'),
                    item('catalog.products', `${APP_PREFIX_PATH}/sales/product-list`, 'Stock & Sources'),
                    ...createRow('catalog.productNew', `${APP_PREFIX_PATH}/catalog/product-new`, 'Create Product Card'),
                    ...createRow('catalog.listingNew', `${APP_PREFIX_PATH}/catalog/listing-new`, 'Add Stock or Source'),
                    item('catalog.pendingReview', `${APP_PREFIX_PATH}/sales/pending-products`, 'Review Queue'),
                    item('catalog.categories', `${APP_PREFIX_PATH}/sales/category-list`, 'Categories'),
                    item('catalog.brands', `${APP_PREFIX_PATH}/sales/brand-list`, 'Brands'),
                    item('catalog.models', `${APP_PREFIX_PATH}/sales/model-pages`, 'Models & Families'),
                    item('catalog.collectionPages', `${APP_PREFIX_PATH}/catalog/model-pages-editor`, 'Collection Pages'),
                    item('catalog.duplicates', `${APP_PREFIX_PATH}/catalog/catalogue-duplicates`, 'Catalogue Duplicates'),
                    item('catalog.catalogueRequests', `${APP_PREFIX_PATH}/catalog/catalogue-requests`, 'Catalogue Requests'),
                    item('catalog.catalogueHealth', `${APP_PREFIX_PATH}/catalog/catalogue-health`, 'Catalogue Health'),
                    item('catalog.sellingTypes', `${APP_PREFIX_PATH}/sales/selling-type-list`, 'Selling Types'),
                    item('catalog.taxonomies', `${APP_PREFIX_PATH}/sales/types-list`, 'Product Attributes'),
                    item('catalog.requests', `${APP_PREFIX_PATH}/suppliers/platform?tab=catalogue`, 'Value Requests'),
                ],
            },

            // ── MERCHANDISING ─────────────────────────────────────────
            {
                key: 'wearit.merchandising',
                path: '',
                title: 'Merchandising',
                translateKey: 'nav.wearit.merchandising',
                icon: 'merchandising',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: [ADMIN],
                subMenu: [
                    item('merch.newArrivals', `${APP_PREFIX_PATH}/merchandising/new-arrivals`, 'New Arrivals'),
                    item('merch.soldProducts', `${APP_PREFIX_PATH}/merchandising/sold-products`, 'Sold Products'),
                    item('merch.collections', `${APP_PREFIX_PATH}/sales/collection-list`, 'Collections'),
                    ...createRow('merch.collectionNew', `${APP_PREFIX_PATH}/sales/collection-new`, 'Add Collection'),
                    item('merch.seasonDrops', `${APP_PREFIX_PATH}/sales/season-collections`, 'Season Drops'),
                    ...createRow('merch.seasonDropNew', `${APP_PREFIX_PATH}/sales/season-collections/new`, 'Create Drop'),
                    item('merch.preorders', `${APP_PREFIX_PATH}/sales/season-collections/preorders`, 'Pre-Orders'),
                    item('merch.banners', `${APP_PREFIX_PATH}/sales/banner-list`, 'Banners'),
                    ...createRow('merch.bannerNew', `${APP_PREFIX_PATH}/sales/banner-new`, 'Add Banner'),
                    item('merch.homepageBuilder', `${APP_PREFIX_PATH}/settings/homepage-builder`, 'Homepage Builder'),
                    ...(features.HPB2
                        ? [item('merch.homepageBuilder2', `${APP_PREFIX_PATH}/settings/homepage-builder-2`, 'Visual Editor (beta)')]
                        : []),
                    item('merch.productPage', `${APP_PREFIX_PATH}/settings/product-page`, 'Product Page'),
                    item('merch.navigation', `${APP_PREFIX_PATH}/settings/navigation`, 'Navigation Manager'),
                    item('merch.homepage', `${APP_PREFIX_PATH}/settings/homepage`, 'App Homepage Sections'),
                    item('merch.lookbooks', `${APP_PREFIX_PATH}/sales/lookbooks`, 'Lookbooks'),
                    item('merch.coupons', `${APP_PREFIX_PATH}/sales/coupon-list`, 'Coupons'),
                    ...createRow('merch.couponNew', `${APP_PREFIX_PATH}/sales/coupon-new`, 'Add Coupon'),
                ],
            },

            // ── WEAR IT INDEX ─────────────────────────────────────────
            {
                key: 'wearit.index',
                path: '',
                // "Index" alone was an internal codename (audit N6).
                title: refined ? 'Market Index' : 'Index',
                translateKey: 'nav.wearit.index',
                icon: 'growth',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: [ADMIN],
                subMenu: [
                    item('index.manager', `${APP_PREFIX_PATH}/market-index`, 'Index Manager'),
                ],
            },

            // ── DIGITAL ASSETS ────────────────────────────────────────
            {
                key: 'wearit.assets',
                path: '',
                title: 'Digital Assets',
                translateKey: 'nav.wearit.assets',
                icon: 'assets',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: [ADMIN, SUPPORT, SHOPPER],
                subMenu: [
                    item('assets.home', `${APP_PREFIX_PATH}/assets/home`, 'Overview', [ADMIN, SUPPORT]),
                    item('assets.library', `${APP_PREFIX_PATH}/assets/library`, 'Asset Library', [ADMIN, SUPPORT]),
                    item('assets.capture', `${APP_PREFIX_PATH}/assets/capture`, 'Capture', [ADMIN, SUPPORT, SHOPPER]),
                    item('assets.intake', `${APP_PREFIX_PATH}/assets/intake`, 'Asset Intake', [ADMIN]),
                ],
            },

            // ── CLIENTS (refined) / CUSTOMERS ─────────────────────────
            // Refined nav merges Customers + Inbox + Requests — the same
            // job ("talk to and understand clients") was split across
            // three workspaces (audit §6, approved). Items keep their own
            // authority, so SUPPORT still sees exactly the inbox pages it
            // saw before, now inside Clients.
            {
                key: 'wearit.customers',
                path: '',
                title: refined ? 'Clients' : 'Customers',
                translateKey: 'nav.wearit.customers',
                icon: 'customers',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: refined ? [ADMIN, SUPPORT] : [ADMIN],
                subMenu: [
                    item('customers.list', `${APP_PREFIX_PATH}/sales/users-list`, 'All Customers'),
                    item('customers.loyalty', `${APP_PREFIX_PATH}/customers/loyalty`, 'Wear It Status'),
                    ...(features.ADMIN_APPROVAL
                        ? [item('customers.pending', `${APP_PREFIX_PATH}/sales/pending-users`, 'Pending Approvals')]
                        : []),
                    ...(refined
                        ? [
                            item('inbox.chats', `${APP_PREFIX_PATH}/sales/chats`, 'Conversations', [ADMIN, SUPPORT]),
                            item('inbox.liveSupport', `${APP_PREFIX_PATH}/sales/live-support`, 'Live Support', [ADMIN, SUPPORT]),
                            item('inbox.visitorChat', `${APP_PREFIX_PATH}/analytics/visitor-chat`, 'Visitor Chat', [ADMIN]),
                            item('inbox.tickets', `${APP_PREFIX_PATH}/sales/support-list`, 'Support Tickets', [ADMIN, SUPPORT]),
                            item('requests.concierge', `${APP_PREFIX_PATH}/concierge`, 'Customer Concierge'),
                            item('requests.insights', `${APP_PREFIX_PATH}/insights`, 'Customer Insights'),
                            item('requests.personalShopping', `${APP_PREFIX_PATH}/sales/personal-shopping`, 'App Requests (legacy)'),
                            item('requests.offers', `${APP_PREFIX_PATH}/sales/offers`, 'Offers'),
                        ]
                        : []),
                ],
            },

            // ── SUPPLIERS ─────────────────────────────────────────────
            {
                key: 'wearit.suppliers',
                path: '',
                title: 'Suppliers',
                translateKey: 'nav.wearit.suppliers',
                icon: 'suppliers',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: [ADMIN],
                subMenu: [
                    item('suppliers.platform', `${APP_PREFIX_PATH}/suppliers/platform`, 'Seller Platform'),
                    item('suppliers.pricingReview', `${APP_PREFIX_PATH}/pricing/review`, 'Pricing Review'),
                    item('suppliers.approved', `${APP_PREFIX_PATH}/users/sellers`, 'Suppliers'),
                    item('suppliers.applications', `${APP_PREFIX_PATH}/users/seller-applications`, 'Applications'),
                    item('suppliers.payouts', `${APP_PREFIX_PATH}/sales/bank-details`, 'Bank Details & Payouts'),
                ],
            },

            // ── INBOX + REQUESTS (pre-refined grouping only) ──────────
            ...(refined
                ? []
                : [
                    {
                        key: 'wearit.inbox',
                        path: '',
                        title: 'Inbox',
                        translateKey: 'nav.wearit.inbox',
                        icon: 'inbox',
                        type: NAV_ITEM_TYPE_COLLAPSE,
                        authority: [ADMIN, SUPPORT],
                        subMenu: [
                            item('inbox.chats', `${APP_PREFIX_PATH}/sales/chats`, 'Conversations', [ADMIN, SUPPORT]),
                            item('inbox.liveSupport', `${APP_PREFIX_PATH}/sales/live-support`, 'Live Support', [ADMIN, SUPPORT]),
                            item('inbox.visitorChat', `${APP_PREFIX_PATH}/analytics/visitor-chat`, 'Visitor Chat', [ADMIN]),
                            item('inbox.tickets', `${APP_PREFIX_PATH}/sales/support-list`, 'Support Tickets', [ADMIN, SUPPORT]),
                        ],
                    },
                    {
                        key: 'wearit.requests',
                        path: '',
                        title: 'Requests',
                        translateKey: 'nav.wearit.requests',
                        icon: 'requests',
                        type: NAV_ITEM_TYPE_COLLAPSE,
                        authority: [ADMIN],
                        subMenu: [
                            item('requests.concierge', `${APP_PREFIX_PATH}/concierge`, 'Customer Concierge'),
                            item('requests.insights', `${APP_PREFIX_PATH}/insights`, 'Customer Insights'),
                            item('requests.personalShopping', `${APP_PREFIX_PATH}/sales/personal-shopping`, 'App Requests (legacy)'),
                            item('requests.offers', `${APP_PREFIX_PATH}/sales/offers`, 'Offers'),
                        ],
                    },
                ]),

            // ── GROWTH ────────────────────────────────────────────────
            {
                key: 'wearit.growth',
                path: '',
                title: 'Growth',
                translateKey: 'nav.wearit.growth',
                icon: 'growth',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: [ADMIN],
                // Ordered by workspace group (Overview → Live → Conversion →
                // Discovery → Acquisition → Campaigns → Customer Voice).
                // The nav system supports two levels, so grouping is by order.
                subMenu: [
                    // Overview
                    item('growth.overview', `${APP_PREFIX_PATH}/analytics`, 'Growth Overview'),
                    // Live
                    item('growth.liveVisitors', `${APP_PREFIX_PATH}/analytics/live-visitors`, 'Live Visitors'),
                    item('growth.liveCheckouts', `${APP_PREFIX_PATH}/growth/live-checkouts`, 'Live Checkouts'),
                    // Conversion
                    item('growth.funnel', `${APP_PREFIX_PATH}/growth/funnel`, 'Sales Funnel'),
                    item('growth.checkoutAnalytics', `${APP_PREFIX_PATH}/growth/checkout-analytics`, 'Checkout Analytics'),
                    item('growth.abandonedCheckouts', `${APP_PREFIX_PATH}/growth/abandoned-checkouts`, 'Revenue Recovery'),
                    item('growth.journey', `${APP_PREFIX_PATH}/growth/journey`, 'Customer Journey'),
                    // Discovery & interest
                    item('growth.search', `${APP_PREFIX_PATH}/growth/search`, 'Search Analytics'),
                    item('growth.wishlist', `${APP_PREFIX_PATH}/growth/wishlist`, 'Wishlist Analytics'),
                    item('growth.productInterest', `${APP_PREFIX_PATH}/analytics/product-interest`, 'Product Interest'),
                    item('growth.behavior', `${APP_PREFIX_PATH}/analytics/behavior`, 'Behaviour'),
                    // Acquisition
                    item('growth.attribution', `${APP_PREFIX_PATH}/growth/attribution`, 'Marketing Attribution'),
                    item('growth.referrals', `${APP_PREFIX_PATH}/sales/referrals`, 'Referrals'),
                    // Campaigns
                    item('growth.waCampaigns', `${APP_PREFIX_PATH}/growth/wa-campaigns`, 'WhatsApp Campaigns'),
                    item('growth.pushCampaigns', `${APP_PREFIX_PATH}/sales/push-campaigns`, 'Push Campaigns'),
                    // Customer voice
                    item('growth.reviews', `${APP_PREFIX_PATH}/sales/reviews`, 'Reviews'),
                ],
            },
        ],
    },

    // ── OPERATIONS / SETTINGS section ─────────────────────────────────
    {
        key: 'wearit.section.operations',
        path: '',
        title: 'Operations',
        translateKey: 'nav.wearit.section.operations',
        icon: '',
        type: NAV_ITEM_TYPE_TITLE,
        authority: [ADMIN],
        subMenu: [
            {
                key: 'wearit.customerExperience',
                path: '',
                title: 'Customer Experience',
                translateKey: 'nav.wearit.customerExperience',
                icon: 'account',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: [ADMIN],
                subMenu: [
                    item('cx.authentication', `${APP_PREFIX_PATH}/customer-experience/authentication`, 'Authentication'),
                    item('cx.accountCentre', `${APP_PREFIX_PATH}/customer-experience/account-centre`, 'Account Centre'),
                    item('cx.supportCentre', `${APP_PREFIX_PATH}/customer-experience/support-centre`, 'Support Centre'),
                    item('cx.helpCentre', `${APP_PREFIX_PATH}/customer-experience/help-centre`, 'Help Centre'),
                    item('cx.refundsPage', `${APP_PREFIX_PATH}/customer-experience/refunds-page`, 'Refunds & Returns'),
                ],
            },
            {
                key: 'wearit.contentManagement',
                path: '',
                title: 'Content Management',
                translateKey: 'nav.wearit.contentManagement',
                icon: 'forms',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: [ADMIN],
                subMenu: [
                    item('content.journal', `${APP_PREFIX_PATH}/content/journal`, 'Journal'),
                    item('content.aboutPage', `${APP_PREFIX_PATH}/content/about-page`, 'About Wear It'),
                    item('content.authenticityPage', `${APP_PREFIX_PATH}/content/authenticity-page`, 'Authenticity Guarantee'),
                ],
            },
            {
                key: 'wearit.finance',
                path: '',
                title: 'Finance & Reports',
                translateKey: 'nav.wearit.finance',
                icon: 'feeMoney',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: [ADMIN],
                subMenu: [
                    item('finance.fees', `${APP_PREFIX_PATH}/fee-management/configuration`, 'Fee Configuration'),
                    item('finance.currency', `${APP_PREFIX_PATH}/fee-management/currency-management`, 'Currency Management'),
                    item('finance.orderReports', `${APP_PREFIX_PATH}/sales/reports/orders`, 'Order Reports'),
                    item('finance.transactionReports', `${APP_PREFIX_PATH}/sales/reports/transactions`, 'Transaction Reports'),
                    item('finance.payoutReports', `${APP_PREFIX_PATH}/sales/reports/payouts`, 'Payout Reports'),
                ],
            },
            {
                key: 'wearit.settings',
                path: '',
                title: 'Settings',
                translateKey: 'nav.wearit.settings',
                icon: 'settings',
                type: NAV_ITEM_TYPE_COLLAPSE,
                authority: [ADMIN],
                subMenu: [
                    item('settings.appConfig', `${APP_PREFIX_PATH}/settings/app-config`, 'App Config'),
                    item('settings.shippingOptions', `${APP_PREFIX_PATH}/shipping/list`, 'Shipping Options'),
                    item('settings.shippingNew', `${APP_PREFIX_PATH}/shipping/new`, 'Add Shipping Option'),
                    item('settings.dhlRateCard', `${APP_PREFIX_PATH}/shipping/dhl-rate-card`, 'DHL Rate Card'),
                    item('settings.staff', `${APP_PREFIX_PATH}/sales/support-users`, 'Staff & Admins'),
                    item('settings.notifications', `${APP_PREFIX_PATH}/sales/notifications`, 'Notifications'),
                    item('settings.faqs', `${APP_PREFIX_PATH}/sales/faqs-list`, 'FAQs'),
                ],
            },
        ],
    },
]

export default wearitNavigationConfig
